import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaCamera,
  FaCar,
  FaHistory,
  FaMicrophone,
  FaSearch,
  FaTimes,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../../auth/AuthContext';
import { useVehicle } from '../../contexts/VehicleContext';
import { discoveryService } from '../../services/apiService';
import SearchResultCard from './SearchResultCard';
import { addRecentSearch, clearRecentSearches, readRecentSearches } from '../../utils/recentSearches';

const SPEECH_SUPPORTED =
  typeof window !== 'undefined' &&
  !!(window.SpeechRecognition || window.webkitSpeechRecognition);

const MODES = [
  { id: 'part', label: 'Part name', icon: FaSearch },
  { id: 'vehicle', label: 'Vehicle', icon: FaCar },
  { id: 'oem', label: 'OEM #', icon: null },
  { id: 'vin', label: 'VIN', icon: null },
  { id: 'photo', label: 'Photo', icon: FaCamera },
];

const DEBOUNCE_MS = 300;

export default function BuyerSearchEngine() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { selectedVehicle, vehicles, selectVehicle } = useVehicle();

  const mode = params.get('mode') || 'part';
  const q = params.get('q') || '';
  const vehicleIdParam = params.get('vehicleId') || '';

  const activeVehicle = useMemo(() => {
    if (vehicleIdParam) {
      return vehicles.find((v) => (v._id || v.id) === vehicleIdParam) || selectedVehicle;
    }
    return selectedVehicle;
  }, [vehicleIdParam, vehicles, selectedVehicle]);

  const vehicleId = activeVehicle?._id || activeVehicle?.id || '';
  const vehicleLabel = activeVehicle
    ? [activeVehicle.make, activeVehicle.model, activeVehicle.year, activeVehicle.variant]
        .filter(Boolean)
        .join(' ')
    : '';

  const [queryInput, setQueryInput] = useState(q);
  const [results, setResults] = useState([]);
  const [relatedParts, setRelatedParts] = useState([]);
  const [vehicleData, setVehicleData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentSearches, setRecentSearches] = useState(() => readRecentSearches());
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [isListening, setIsListening] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [tookMs, setTookMs] = useState(null);

  const recognitionRef = useRef(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    setQueryInput(q);
  }, [q]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!containerRef.current?.contains(e.target)) {
        setShowSuggestions(false);
        setActiveSuggestionIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const setMode = (nextMode) => {
    const next = new URLSearchParams(params);
    next.set('mode', nextMode);
    setParams(next);
    setResults([]);
    setVehicleData(null);
    setError('');
  };

  const runAutocomplete = useCallback(async (prefix) => {
    const trimmed = String(prefix || '').trim();
    if (trimmed.length < 2 || mode !== 'part') {
      setSuggestions([]);
      return;
    }
    try {
      const res = await discoveryService.searchAutocomplete(trimmed);
      const data = res?.data || res;
      setSuggestions(data?.suggestions || []);
    } catch {
      setSuggestions([]);
    }
  }, [mode]);

  useEffect(() => {
    const timer = setTimeout(() => runAutocomplete(queryInput), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [queryInput, runAutocomplete]);

  const runSearch = useCallback(async () => {
    setLoading(true);
    setError('');
    setTookMs(null);
    setRelatedParts([]);

    try {
      if (mode === 'part') {
        const trimmed = queryInput.trim();
        if (trimmed.length < 2) {
          setResults([]);
          setLoading(false);
          return;
        }
        const res = await discoveryService.unifiedSearch({
          mode: 'part',
          q: trimmed,
          vehicleId: vehicleId || undefined,
        });
        const data = res?.data || res;
        setResults(data?.results || []);
        setRelatedParts(data?.relatedParts || []);
        setTookMs(data?.tookMs ?? null);
        addRecentSearch(trimmed);
        setRecentSearches(readRecentSearches());
        return;
      }

      if (mode === 'vehicle') {
        const vid = vehicleIdParam || vehicleId;
        if (!vid) {
          setVehicleData(null);
          setResults([]);
          setLoading(false);
          return;
        }
        const res = await discoveryService.unifiedSearch({ mode: 'vehicle', vehicleId: vid });
        const data = res?.data || res;
        setVehicleData(data);
        setResults([]);
        return;
      }

      if (mode === 'oem') {
        const oem = queryInput.trim();
        if (oem.length < 4) {
          setResults([]);
          setLoading(false);
          return;
        }
        const res = await discoveryService.unifiedSearch({ mode: 'oem', oem });
        const data = res?.data || res;
        if (data?.matched && data?.directToNode && data?.catalogNodeId) {
          navigate(`/catalog/node/${data.catalogNodeId}`);
          return;
        }
        setResults(data?.results || []);
        if (!data?.matched) {
          setError(data?.message || 'No results for this OEM number. Search by part name instead?');
        }
        addRecentSearch(oem);
        setRecentSearches(readRecentSearches());
        return;
      }

      if (mode === 'vin') {
        if (!isAuthenticated) {
          setError('Sign in to search by VIN');
          setResults([]);
          setLoading(false);
          return;
        }
        const vinQuery = queryInput.trim();
        const isShort = vinQuery.length <= 8;
        const res = await discoveryService.unifiedSearch({
          mode: 'vin',
          vehicleId: vehicleIdParam || undefined,
          vinLast6: isShort ? vinQuery : undefined,
          vin: !isShort ? vinQuery : undefined,
        });
        const data = res?.data || res;
        if (!data?.matched) {
          setError(data?.message || 'Vehicle not found in your garage.');
          setResults([]);
          setVehicleData(null);
          return;
        }
        setVehicleData(data);
        setResults(data?.results || []);
        return;
      }

      if (mode === 'photo') {
        await discoveryService.logPhotoSearchTap();
        return;
      }
    } catch (err) {
      setError(err?.message || 'Search failed');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [
    mode,
    queryInput,
    vehicleId,
    vehicleIdParam,
    isAuthenticated,
    navigate,
  ]);

  useEffect(() => {
    if (mode === 'vehicle' && (vehicleIdParam || vehicleId)) {
      runSearch();
    } else if (mode === 'part' && q.trim().length >= 2) {
      runSearch();
    } else if (mode === 'oem' && q.trim().length >= 4) {
      runSearch();
    }
  }, [mode, q, vehicleIdParam, vehicleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitSearch = (e) => {
    e?.preventDefault();
    setShowSuggestions(false);
    setActiveSuggestionIndex(-1);

    if (mode === 'photo') {
      discoveryService.logPhotoSearchTap().catch(() => {});
      toast.info('Photo search coming soon');
      return;
    }

    const next = new URLSearchParams(params);
    const trimmed = queryInput.trim();
    if (mode === 'part' || mode === 'oem') {
      if (trimmed) next.set('q', trimmed);
      else next.delete('q');
    }
    if (vehicleId) next.set('vehicleId', vehicleId);
    setParams(next);

    if (mode !== 'part' && mode !== 'oem') {
      runSearch();
    }
  };

  const selectSuggestion = (term) => {
    setQueryInput(term);
    setShowSuggestions(false);
    const next = new URLSearchParams(params);
    next.set('q', term);
    if (vehicleId) next.set('vehicleId', vehicleId);
    setParams(next);
  };

  const stopListening = () => {
    recognitionRef.current?.stop();
    setIsListening(false);
  };

  const startListening = () => {
    if (!SPEECH_SUPPORTED) return;
    stopListening();
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-IN';
    recognition.interimResults = true;
    recognition.continuous = false;
    recognitionRef.current = recognition;

    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        transcript += event.results[i][0].transcript;
      }
      const next = transcript.trim();
      if (next) {
        setQueryInput(next);
        setShowSuggestions(true);
      }
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognition.start();
    setIsListening(true);
    setShowSuggestions(true);
  };

  useEffect(() => () => stopListening(), []);

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter') submitSearch(e);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestionIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestionIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeSuggestionIndex >= 0) {
        selectSuggestion(suggestions[activeSuggestionIndex]);
      } else {
        submitSearch(e);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const requestNotify = async (partName) => {
    if (!vehicleId) {
      toast.info('Add your vehicle first so we can notify you');
      navigate('/account/vehicles');
      return;
    }
    if (!isAuthenticated) {
      toast.info('Log in to get notified');
      navigate('/login');
      return;
    }
    setRequesting(true);
    try {
      const res = await discoveryService.requestCatalogNode({
        vehicleId,
        partCategory: partName || queryInput.trim(),
        searchQuery: queryInput.trim(),
      });
      const data = res?.data || res;
      if (data.alreadyExists && data.catalogNodeId) {
        navigate(`/catalog/node/${data.catalogNodeId}?vehicleId=${vehicleId}`);
        return;
      }
      toast.success(data.message || 'We will notify you when this part is available');
    } catch (err) {
      toast.error(err?.message || 'Could not save notification');
    } finally {
      setRequesting(false);
    }
  };

  const selectVehicleForBrowse = (v) => {
    const id = v._id || v.id;
    selectVehicle?.(v);
    const next = new URLSearchParams(params);
    next.set('mode', 'vehicle');
    next.set('vehicleId', id);
    setParams(next);
  };

  const placeholders = {
    part: 'Search parts — e.g. "Swift ka brake pad", "oil filter"',
    oem: 'Enter OEM part number',
    vin: 'Full VIN or chassis last 6 digits',
  };

  return (
    <div className="mx-auto min-h-screen max-w-3xl px-4 pb-24 pt-4 sm:px-6">
      <h1 className="text-2xl font-bold text-slate-900 mb-4">Find parts</h1>

      <div className="flex gap-1 overflow-x-auto pb-2 mb-4 scrollbar-hide">
        {MODES.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setMode(id)}
            className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition ${
              mode === id
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'bg-white border border-slate-200 text-slate-600 hover:border-indigo-300'
            }`}
          >
            {Icon && <Icon className="text-xs" />}
            {label}
          </button>
        ))}
      </div>

      {mode === 'photo' && (
        <button
          type="button"
          onClick={() => {
            discoveryService.logPhotoSearchTap().catch(() => {});
            toast.info('Photo search coming soon');
          }}
          className="w-full rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 p-12 text-center hover:border-indigo-400 hover:bg-indigo-50/50 transition"
        >
          <FaCamera className="mx-auto text-4xl text-slate-400 mb-3" />
          <p className="text-lg font-bold text-slate-700">Photo search coming soon</p>
          <p className="text-sm text-slate-500 mt-2">
            Snap a photo of any part and we will find a match — launching soon.
          </p>
        </button>
      )}

      {mode !== 'photo' && mode !== 'vehicle' && (
        <form onSubmit={submitSearch} className="mb-4">
          <div ref={containerRef} className="relative">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  ref={inputRef}
                  type="search"
                  value={queryInput}
                  onChange={(e) => setQueryInput(e.target.value)}
                  onFocus={() => setShowSuggestions(true)}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholders[mode] || 'Search…'}
                  className="w-full rounded-xl border border-slate-200 py-3 pl-10 pr-12 text-sm shadow-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                  autoComplete="off"
                />
                {SPEECH_SUPPORTED && (
                  <button
                    type="button"
                    onClick={isListening ? stopListening : startListening}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-2 ${
                      isListening ? 'text-red-500 bg-red-50' : 'text-slate-400 hover:text-indigo-600'
                    }`}
                    aria-label={isListening ? 'Stop voice search' : 'Voice search'}
                  >
                    <FaMicrophone />
                  </button>
                )}
              </div>
              <button
                type="submit"
                className="shrink-0 rounded-xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white hover:bg-indigo-700"
              >
                Search
              </button>
            </div>

            {showSuggestions && mode === 'part' && queryInput.trim().length >= 2 && suggestions.length > 0 && (
              <ul className="absolute z-20 mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden">
                {suggestions.map((s, idx) => (
                  <li key={s}>
                    <button
                      type="button"
                      onClick={() => selectSuggestion(s)}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-indigo-50 ${
                        idx === activeSuggestionIndex ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700'
                      }`}
                    >
                      {s}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {showSuggestions && mode === 'part' && !queryInput.trim() && recentSearches.length > 0 && (
              <div className="absolute z-20 mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-lg p-2">
                <div className="flex items-center justify-between px-2 py-1">
                  <span className="text-xs font-semibold uppercase text-slate-400">Recent</span>
                  <button
                    type="button"
                    onClick={() => {
                      clearRecentSearches();
                      setRecentSearches([]);
                    }}
                    className="text-xs text-slate-400 hover:text-red-500"
                  >
                    Clear
                  </button>
                </div>
                {recentSearches.map((term) => (
                  <button
                    key={term}
                    type="button"
                    onClick={() => selectSuggestion(term)}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    <FaHistory className="text-slate-300 shrink-0" />
                    {term}
                  </button>
                ))}
              </div>
            )}
          </div>
        </form>
      )}

      {mode === 'vehicle' && (
        <div className="mb-6">
          <p className="text-sm text-slate-600 mb-3">
            Select a saved vehicle to browse all parts grouped by sub-system.
          </p>
          {vehicles.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-center">
              <p className="text-slate-600 mb-3">No vehicles saved yet.</p>
              <Link to="/account/vehicles" className="text-indigo-600 font-semibold hover:underline">
                Add your vehicle
              </Link>
            </div>
          ) : (
            <ul className="space-y-2">
              {vehicles.map((v) => {
                const id = v._id || v.id;
                const label = [v.make, v.model, v.year, v.variant].filter(Boolean).join(' · ');
                const active = id === (vehicleIdParam || vehicleId);
                return (
                  <li key={id}>
                    <button
                      type="button"
                      onClick={() => selectVehicleForBrowse(v)}
                      className={`w-full text-left rounded-xl border p-4 transition ${
                        active
                          ? 'border-indigo-400 bg-indigo-50'
                          : 'border-slate-200 bg-white hover:border-indigo-300'
                      }`}
                    >
                      <p className="font-semibold text-slate-900">{label}</p>
                      {v.registrationNumber && (
                        <p className="text-xs text-slate-500 mt-0.5">{v.registrationNumber}</p>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <Link
            to="/discovery/my-vehicle"
            className="mt-4 inline-block text-sm font-semibold text-indigo-600 hover:underline"
          >
            Full vehicle parts browse →
          </Link>
        </div>
      )}

      {vehicleLabel && mode === 'part' && (
        <p className="mb-3 text-xs text-indigo-700 bg-indigo-50 rounded-lg px-3 py-2">
          Results scoped to <strong>{vehicleLabel}</strong>
        </p>
      )}

      {error && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {error}
          {mode === 'oem' && (
            <button
              type="button"
              onClick={() => setMode('part')}
              className="ml-2 font-semibold underline"
            >
              Search by part name
            </button>
          )}
        </div>
      )}

      {loading && <p className="py-12 text-center text-slate-500">Searching…</p>}

      {!loading && mode === 'part' && tookMs != null && results.length > 0 && (
        <p className="mb-3 text-xs text-slate-400">{results.length} results · {tookMs}ms</p>
      )}

      {!loading && mode === 'part' && results.length > 0 && (
        <ul className="space-y-3">
          {results.map((result) => (
            <li key={result.catalogNodeId}>
              <SearchResultCard result={result} vehicleId={vehicleId} />
            </li>
          ))}
        </ul>
      )}

      {!loading && mode === 'part' && q.trim().length >= 2 && results.length === 0 && !error && (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
          <h2 className="text-xl font-bold text-slate-900">No results for &ldquo;{q}&rdquo;</h2>
          <p className="mt-2 text-sm text-slate-500">Try a different term or browse related parts below.</p>
          <button
            type="button"
            onClick={() => requestNotify(q)}
            disabled={requesting}
            className="mt-6 inline-block rounded-xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            {requesting ? 'Saving…' : 'Notify me when available'}
          </button>
          {relatedParts.length > 0 && (
            <div className="mt-8 text-left">
              <h3 className="text-sm font-bold uppercase text-slate-500 mb-3">Related parts</h3>
              <ul className="space-y-2">
                {relatedParts.map((r) => (
                  <li key={r.catalogNodeId}>
                    <Link
                      to={`/catalog/node/${r.catalogNodeId}${vehicleId ? `?vehicleId=${vehicleId}` : ''}`}
                      className="block rounded-lg border border-slate-100 px-4 py-3 text-sm hover:border-indigo-200"
                    >
                      {r.partCategory}
                      {r.vehicleHint && (
                        <span className="text-slate-400 ml-2">· {r.vehicleHint}</span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {!loading && mode === 'oem' && results.length > 0 && (
        <ul className="space-y-3">
          {results.map((result) => (
            <li key={result.catalogNodeId}>
              <SearchResultCard result={result} vehicleId={vehicleId} />
            </li>
          ))}
        </ul>
      )}

      {!loading && mode === 'vehicle' && vehicleData?.subSystems && (
        <div className="space-y-4">
          {vehicleData.vehicle && (
            <p className="text-sm text-slate-600">
              {[vehicleData.vehicle.make, vehicleData.vehicle.model, vehicleData.vehicle.year]
                .filter(Boolean)
                .join(' · ')}
              {vehicleData.catalogNodeCount != null && (
                <span className="text-slate-400"> · {vehicleData.catalogNodeCount} parts</span>
              )}
            </p>
          )}
          {vehicleData.previouslyOrderedParts?.length > 0 && (
            <section>
              <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-2">
                <FaHistory /> Ordered before
              </h2>
              <ul className="space-y-2">
                {vehicleData.previouslyOrderedParts.map((part) => (
                  <li key={part.catalogNodeId}>
                    <Link
                      to={`/catalog/node/${part.catalogNodeId}?vehicleId=${vehicleIdParam || vehicleId}`}
                      className="block rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm hover:border-indigo-300"
                    >
                      {part.partCategory}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <section>
            <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-2">
              Browse by sub-system
            </h2>
            <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {vehicleData.subSystems
                .filter((ss) => (ss.partCount || 0) > 0)
                .map((ss) => (
                  <li key={ss.name}>
                    <Link
                      to={`/discovery/subsystem/${encodeURIComponent(ss.name)}?vehicleId=${vehicleIdParam || vehicleId}`}
                      className="block rounded-xl border border-slate-200 bg-white p-4 text-center hover:border-indigo-300 transition"
                    >
                      <p className="text-sm font-semibold text-slate-900">{ss.name}</p>
                      <p className="text-xs text-slate-500 mt-1">{ss.partCount} parts</p>
                    </Link>
                  </li>
                ))}
            </ul>
          </section>
        </div>
      )}

      {!loading && mode === 'vin' && vehicleData?.matched !== false && results.length > 0 && (
        <>
          {vehicleData?.decode && (
            <p className="mb-4 text-sm text-slate-600">
              Decoded: {[vehicleData.decode.make, vehicleData.decode.model, vehicleData.decode.year]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )}
          <ul className="space-y-3">
            {results.map((result) => (
              <li key={result.catalogNodeId}>
                <SearchResultCard result={result} vehicleId={vehicleIdParam || vehicleId} />
              </li>
            ))}
          </ul>
        </>
      )}

      {mode === 'vin' && !isAuthenticated && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-center">
          <p className="text-slate-600 mb-3">Sign in to search by VIN from your saved vehicles.</p>
          <Link to="/login" className="text-indigo-600 font-semibold hover:underline">
            Sign in
          </Link>
        </div>
      )}
    </div>
  );
}
